import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  BestResultInterface,
  EduContent,
  Result,
  ResultInterface,
  ResultsPermissions,
  ResultStatusEnum,
  TasksPermissions,
  SettingsPermissions,
} from '@campus/dal';

import {
  ContentActionInterface,
  ContentBundleActionsServiceInterface,
  ContentOpenActionsServiceInterface,
  ContentTaskActionsServiceInterface,
  CONTENT_BUNDLE_ACTIONS_SERVICE_TOKEN,
  CONTENT_OPEN_ACTIONS_SERVICE_TOKEN,
  CONTENT_TASK_ACTIONS_SERVICE_TOKEN,
  EduContentSearchResultInterface,
  PermissionService,
} from '@campus/shared';
import '@diekeure/dcr-components/dcr-card.js';
import '@diekeure/dcr-components/dcr-inline-button.js';
import '@diekeure/dcr-components/dcr-inline-icon-button.js';
import '@diekeure/dcr-components/dcr-menu-item.js';
import '@diekeure/dcr-components/dcr-menu.js';
import '@diekeure/dcr-components/dcr-score.js';
import '@diekeure/dcr-components/dcr-tonal-button.js';
import { shareReplay, take } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Component({
    selector: 'campus-toc-result-grid-item',
    templateUrl: './toc-result-grid-item.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TocResultGridItemComponent implements OnInit, OnChanges {
  constructor(private permissionService: PermissionService) {}

  @Input() data: EduContentSearchResultInterface;
  @Input() selected = false;
  @Input()
  set inBasket(value) {
    if (value) this.selected = true;
  }

  @Output() selectionChange = new EventEmitter<{ value: EduContentSearchResultInterface; checked: boolean }>();

  private contentOpenActionsService = inject<ContentOpenActionsServiceInterface>(CONTENT_OPEN_ACTIONS_SERVICE_TOKEN);
  private contentTaskActionsService = inject<ContentTaskActionsServiceInterface>(CONTENT_TASK_ACTIONS_SERVICE_TOKEN, {
    optional: true,
  });
  private contentBundleActionsService = inject<ContentBundleActionsServiceInterface>(
    CONTENT_BUNDLE_ACTIONS_SERVICE_TOKEN,
    {
      optional: true,
    }
  );
  private permissionsService = inject(PermissionService);
  private actionsMap = new Map<number, ContentActionInterface[]>();

  public isStudent$ = this.permissionsService.hasPermission$(ResultsPermissions.SHOW_STUDENT_SCORE);
  public canManageTaskContentV2$: Observable<boolean>;

  public primaryActionMap = new Map<number, ContentActionInterface>();
  public secondaryActionsMap = new Map<number, ContentActionInterface[]>();
  public bestScoresMap: Map<number, number | null> = new Map();
  public eduContentIconsMap = new Map<number, string>();
  public statusTaskMap: Map<number, boolean | false> = new Map();
  public SettingsPermissions = SettingsPermissions;

  private readonly cd = inject(ChangeDetectorRef);

  // TODO: awaiting findings Karl on how to use general app eduContent icons in the src
  private readonly actionIconMap = {
    'show-solution': 'content-open-actions:show-solution',
    'add-to-task': 'content-task:add-to-task',
    'remove-from-task': 'content-task:remove-from-task',
    open_in_new: 'content-open-actions:open-in-new',
    book: 'content-open-actions:book',
    preview: 'content-open-actions:preview',
  } as const;

  // TODO: awaiting findings Karl on how to use general app eduContent icons in the src
  private readonly eduContentIconMap = {
    'file-type:digitale oefening': 'educontent:file-type:exercise',
    'file-type:algebrakit': 'educontent:file-type:algebrakit',
    'file-type:tijdslijn': 'educontent:file-type:timeline',
    'file-type:bronmateriaal': 'educontent:file-type:link',
    'file-type:geogebra': 'educontent:file-type:geogebra',
  } as const;

  ngOnInit() {
    this.refreshMaps();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.refreshMaps();
    }

    this.canManageTaskContentV2$ = this.permissionService
      .hasPermission$(TasksPermissions.MANAGE_TASK_CONTENT_V2)
      .pipe(take(1), shareReplay(1));
  }

  public handleAction(action: ContentActionInterface, eduContent: EduContent) {
    if (action) {
      action.handler(eduContent, action.options);
    }
  }

  public onSelectionChange(checked: boolean, searchResult: EduContentSearchResultInterface) {
    this.selected = checked;
    this.selectionChange.emit({ value: searchResult, checked });
  }

  private refreshMaps(): void {
    const data = this.data;
    if (!data?.eduContent) return;

    const eduContentId = data.eduContent.id;

    this.enrichEduContentWithIcon(data.eduContent);

    const actions = [...this.getTaskActions(data), ...this.getBundleActions(data), ...this.getContentActions(data)];

    this.primaryActionMap.set(
      eduContentId,
      actions.find((a) => a.isPrimary)
    );
    this.secondaryActionsMap.set(
      eduContentId,
      actions.filter((a) => !a.isPrimary).map((a) => this.enrichActionWithIcon(a))
    );

    this.actionsMap.set(eduContentId, actions);
    this.updateBestScoreForContent(eduContentId, data.results);
    this.updateStatusForContent(eduContentId, data.results);

    this.cd.markForCheck();
  }

  private getContentActions(data: EduContentSearchResultInterface): ContentActionInterface[] {
    const completedResult = this.findCompletedResult(data);
    const isCompletedExercise = this.isCompletedExercise(data.eduContent, completedResult);

    if (isCompletedExercise && completedResult) {
      return this.getActionsForCompletedExercise(data.eduContent, completedResult, data.bestResult, data);
    }

    return this.contentOpenActionsService.getActionsForContent(data.eduContent, data.result);
  }

  private getTaskActions(data: EduContentSearchResultInterface): ContentActionInterface[] {
    if (!data.addTaskActions) return [];
    if (!this.contentTaskActionsService) return [];

    return this.contentTaskActionsService.getTaskActionsForEduContent(data.eduContent, data.inBasket);
  }

  private getBundleActions(data: EduContentSearchResultInterface): ContentActionInterface[] {
    if (!data.addBundleActions) return [];
    if (!this.contentBundleActionsService) return [];

    return this.contentBundleActionsService.getBundleActionsForEduContent(data.eduContent, data.inBasket);
  }

  private updateBestScoreForContent(eduContentId: number, results: ResultInterface[]): void {
    const best = Result.toBestResult(results);

    let score = null;
    if (best && Array.isArray(results) && results.length > 0) {
      const hasCompletedResults = results.some(
        (result) =>
          result.status === ResultStatusEnum.STATUS_COMPLETED || result.status === ResultStatusEnum.STATUS_OPENED
      );

      if (hasCompletedResults) {
        score = best.score ?? null;
      }
    }

    this.bestScoresMap.set(eduContentId, score);
  }

  private updateStatusForContent(eduContentId: number, results: ResultInterface[]): void {
    const hasPending = Array.isArray(results)
      ? results.some((result) => result.status === ResultStatusEnum.STATUS_INCOMPLETE)
      : false;

    this.statusTaskMap.set(eduContentId, hasPending);
  }

  private enrichActionWithIcon(action: ContentActionInterface): ContentActionInterface & { resolvedIcon: string } {
    return {
      ...action,
      resolvedIcon:
        this.actionIconMap[action.icon as keyof typeof this.actionIconMap] ?? 'content-open-actions:preview',
    };
  }

  private enrichEduContentWithIcon(eduContent: EduContent): void {
    const icon = 'educontent:' + eduContent.icon;
    this.eduContentIconsMap.set(eduContent.id, icon);
  }

  private isCompletedExercise(eduContent: EduContent, result: ResultInterface | undefined): boolean {
    return (
      eduContent?.isExercise &&
      result &&
      [ResultStatusEnum.STATUS_COMPLETED, ResultStatusEnum.STATUS_OPENED].includes(result.status)
    );
  }

  private findCompletedResult(eduContentSearch: EduContentSearchResultInterface): ResultInterface | undefined {
    // Check the regular result first
    const regularResult = eduContentSearch.result;
    if (
      regularResult &&
      [ResultStatusEnum.STATUS_COMPLETED, ResultStatusEnum.STATUS_OPENED].includes(regularResult.status)
    ) {
      return regularResult;
    }

    // Look through results array to find a completed one
    const results = eduContentSearch.results;
    if (Array.isArray(results)) {
      return results.find((result) =>
        [ResultStatusEnum.STATUS_COMPLETED, ResultStatusEnum.STATUS_OPENED].includes(result.status)
      );
    }

    return undefined;
  }

  private getActionsForCompletedExercise(
    eduContent: EduContent,
    result: ResultInterface,
    bestResult?: BestResultInterface,
    eduContentSearch?: EduContentSearchResultInterface
  ): ContentActionInterface[] {
    // Calculate the actual best result from results array if bestResult is undefined
    // This handles cases where the data store hasn't been updated properly after exercise completion
    let actualBestResult = bestResult;
    if (!bestResult && eduContentSearch?.results && Array.isArray(eduContentSearch.results)) {
      const calculatedBest = Result.toBestResult(eduContentSearch.results);
      if (calculatedBest && calculatedBest.score !== undefined) {
        actualBestResult = calculatedBest;
      }
    }

    // Use actualBestResult if available, otherwise fall back to the provided result
    // This ensures both regular and review actions use the latest/best data
    const resultToUse = actualBestResult || result;

    const regularActions = this.contentOpenActionsService.getActionsForContent(eduContent, resultToUse);
    const reviewActions = this.contentOpenActionsService.getActionsForContent(eduContent, resultToUse, {
      forReview: true,
    });

    const reviewAction = this.findReviewAction(reviewActions);

    if (reviewAction) {
      const secondaryReviewAction = this.createSecondaryReviewAction(reviewAction, resultToUse);
      return [...regularActions, secondaryReviewAction];
    }

    return regularActions;
  }

  private findReviewAction(reviewActions: ContentActionInterface[]): ContentActionInterface | undefined {
    return reviewActions.find((action) => action.label.includes('Herbekijk') || action.label.includes('review'));
  }

  private createSecondaryReviewAction(
    reviewAction: ContentActionInterface,
    result: ResultInterface | BestResultInterface
  ): ContentActionInterface {
    return {
      ...reviewAction,
      isPrimary: false,
      options: { forResult: result },
    };
  }
}
